import { bookableAreasClosed } from "../../types/venues";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import { errorHandler, apiFetchNotOkHandler } from "../errorHandler";
import { getBuildVersion } from "../getDefaultHeaders";

type UpdateClosedAreaProps = {
  venueId: string;
  closedArea: bookableAreasClosed;
};

type UpdateClosedAreaResponse = bookableAreasClosed | undefined;

type WeekdayKey =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

const WEEKDAY_KEYS: WeekdayKey[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

type CollinsApiClosedAreaPayload = {
  id?: string;
  type: "areas" | "zone";
  date_type?: "weekdays" | "date_range";
  weekdays?: string[];
  time_from?: string;
  time_to?: string;
  areas?: string[];
  date_from?: string;
  date_to?: string;
  zone?: string;
  message?: string;
};

type CollinsApiClosedAreaResponse = {
  closed_area: CollinsApiClosedAreaPayload;
};

const toCollinsApiPayload = (
  closedArea: bookableAreasClosed
): CollinsApiClosedAreaPayload => {
  const weekdaysArray = closedArea.weekdays
    ? WEEKDAY_KEYS.filter((day) => closedArea.weekdays?.[day])
    : undefined;

  return {
    id: closedArea.id,
    type: closedArea.type,
    date_type: closedArea.date_type,
    weekdays: weekdaysArray?.length ? weekdaysArray : undefined,
    time_from: closedArea.time_from,
    time_to: closedArea.time_to,
    areas: closedArea.areas,
    date_from: closedArea.date_from,
    date_to: closedArea.date_to,
    zone: closedArea.zone,
    message: closedArea.message ?? ""
  };
};

const fromCollinsApiResponse = (
  apiClosedArea: CollinsApiClosedAreaPayload
): bookableAreasClosed => {
  const weekdaysObject = apiClosedArea.weekdays
    ? WEEKDAY_KEYS.reduce(
        (acc, day) => {
          if (apiClosedArea.weekdays?.includes(day)) {
            acc[day] = true;
          }
          return acc;
        },
        {} as Record<WeekdayKey, boolean>
      )
    : undefined;

  return {
    id: apiClosedArea.id ?? "",
    type: apiClosedArea.type,
    date_type: apiClosedArea.date_type as "weekdays" | undefined,
    weekdays: weekdaysObject,
    time_from: apiClosedArea.time_from,
    time_to: apiClosedArea.time_to,
    areas: apiClosedArea.areas,
    date_from: apiClosedArea.date_from,
    date_to: apiClosedArea.date_to,
    zone: apiClosedArea.zone,
    message: apiClosedArea.message
  };
};

export const updateClosedArea = async ({
  venueId,
  closedArea
}: UpdateClosedAreaProps): Promise<UpdateClosedAreaResponse> => {
  if (!venueId) {
    return undefined;
  }

  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const isUpdate = !!closedArea.id;
  const url = isUpdate
    ? `${environmentVars?.COLLINS_API_BASE_URL}/venues/${venueId}/closed-areas/${closedArea.id}`
    : `${environmentVars?.COLLINS_API_BASE_URL}/venues/${venueId}/closed-areas`;

  const method = isUpdate ? "PUT" : "POST";

  const payload = toCollinsApiPayload(closedArea);

  try {
    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Build-Version": getBuildVersion()
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error updating closed area");
      return undefined;
    }

    const data: CollinsApiClosedAreaResponse = await response.json();
    return fromCollinsApiResponse(data.closed_area);
  } catch (error) {
    errorHandler(error, "Error updating closed area");
    return undefined;
  }
};

type DeleteClosedAreaProps = {
  venueId: string;
  closedAreaId: string;
};

export const deleteClosedArea = async ({
  venueId,
  closedAreaId
}: DeleteClosedAreaProps): Promise<boolean> => {
  if (!venueId || !closedAreaId) {
    return false;
  }

  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const url = `${environmentVars?.COLLINS_API_BASE_URL}/venues/${venueId}/closed-areas/${closedAreaId}`;

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "X-Build-Version": getBuildVersion()
      }
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error deleting closed area");
      return false;
    }

    return true;
  } catch (error) {
    errorHandler(error, "Error deleting closed area");
    return false;
  }
};
